/**
 * 标准配合最终约束校验器。
 *
 * 文件职责：
 * 1. 在顺序求解完成后复核最终几何是否仍满足每条已求解配合；
 * 2. 将“后续配合覆盖前序配合”的情况显式升级为 overDefined 诊断；
 * 3. 保持校验输入为可序列化 Mate Definition 与 transform 快照，不依赖 UI 或 Three.js 场景对象。
 */

import * as THREE from 'three';
import type { ComponentTransformSnapshot } from '../../component-instance';
import {
  AssemblyMateType,
  MateAlignment,
  MateConcentricMisalignmentType,
  MateEntityKind,
  MateSolveStatus,
  type AssemblyMateDefinition,
  type MateEntityRef,
} from '../../definition';
import type {
  AssemblyMateSolveDiagnostic,
  AssemblyMateSolverComponentState,
} from '../StandardMateSolverTypes';
import type { ComponentWorldDeltaMap } from '../geometry/StandardMateGeometryTransform';
import { transformMateDefinitionForSolve } from '../geometry/StandardMateGeometryTransform';
import {
  resolveStandardMateLinearDimensionScale,
  resolveStandardMateSignedDistance,
} from '../dimension/StandardMateDimension';
import { resolveStandardMateDistanceOffsetForSolver } from '../dimension/StandardMateDistanceTarget';
import {
  composeMatrix,
  resolveAxisGeometry,
  resolveConeGeometry,
  resolvePlaneGeometry,
  resolvePointGeometry,
  resolveRepresentativePointGeometry,
  resolveSphereGeometry,
  type AxisGeometry,
  type ConeGeometry,
  type PlaneGeometry,
  type SphereGeometry,
} from '../geometry/StandardMateGeometryAccess';
import {
  resolveAxisTangentRadius,
  resolveConeSphereSignedDistance,
} from '../geometry/StandardMateTangentGeometry';
import {
  isRoundRoundTangentInternalBranchDegenerate,
  resolveAlignedPlaneRoundTangentDistance,
  resolveAlignedRoundRoundTangentDistance,
} from '../geometry/StandardMateTangentAlignment';
import { validateSurfaceCoincidentMate } from '../StandardMateSurfaceCoincidentValidation';
import { validateReferencedAngleMate } from './StandardMateAngleReferenceValidator';
import { validateRedundantMateDefinitions } from './StandardMateRedundantConstraintValidator';
import {
  STANDARD_MATE_RESIDUAL_FAILURE_DISTANCE_BRANCH_SIDE,
  STANDARD_MATE_RESIDUAL_FAILURE_GENERAL,
  hasResidualConflictPeer,
  type StandardMateResidualFailureKind,
} from './StandardMateResidualConflictPolicy';
import {
  DEFAULT_STANDARD_MATE_ANGULAR_TOLERANCE,
  evaluateStandardMateConcentricResidual,
} from '../StandardMateConstraintResidual';

/** 默认角度校验容差。 */
const DEFAULT_VALIDATION_ANGLE_EPSILON = DEFAULT_STANDARD_MATE_ANGULAR_TOLERANCE;

/**
 * 单条 Mate 约束最终残差校验结果。
 */
interface StandardMateConstraintValidationResult {
  /** 当前几何是否满足该 Mate；null 表示当前校验器无法验证。 */
  readonly satisfied: boolean | null;
  /** 不满足时的归因类型，用于决定是否能升级为 overDefined。 */
  readonly failureKind: StandardMateResidualFailureKind;
}

/**
 * 同轴心错位最终校验策略。
 */
interface ConcentricMisalignmentValidationPolicy {
  /** 当前 Mate 允许的最大轴线/中心偏差。 */
  readonly maximumDeviation: number;
}

/**
 * 创建标准配合残差校验结果。
 *
 * @param satisfied 当前几何是否满足 Mate。
 * @param failureKind 不满足时的归因类型。
 * @returns 标准化校验结果。
 */
function createConstraintValidationResult(
  satisfied: boolean | null,
  failureKind: StandardMateResidualFailureKind = STANDARD_MATE_RESIDUAL_FAILURE_GENERAL,
): StandardMateConstraintValidationResult {
  return {
    satisfied,
    failureKind,
  };
}

/**
 * 创建点/球 Distance 校验结果。
 *
 * @param distanceSatisfied 绝对距离是否满足。
 * @param branchSatisfied 尺寸侧是否满足；null 表示当前几何没有尺寸侧语义。
 * @returns 标准化校验结果。
 */
function createPointDistanceValidationResult(
  distanceSatisfied: boolean,
  branchSatisfied: boolean | null,
): StandardMateConstraintValidationResult {
  if (!distanceSatisfied) {
    return createConstraintValidationResult(false);
  }
  if (branchSatisfied === false) {
    return createConstraintValidationResult(false, STANDARD_MATE_RESIDUAL_FAILURE_DISTANCE_BRANCH_SIDE);
  }
  return createConstraintValidationResult(true);
}

/**
 * 判断 Mate 是否为具备 linkedMateId 和最大偏差的同轴心错位 Mate。
 *
 * @param mate Mate Definition。
 * @returns 是否可参与错位同轴心校验。
 */
function isValidConcentricMisalignmentMate(mate: AssemblyMateDefinition): boolean {
  return mate.mateType === AssemblyMateType.Concentric
    && typeof mate.concentricMisalignment?.linkedMateId === 'string'
    && mate.concentricMisalignment.linkedMateId.length > 0
    && typeof mate.concentricMisalignment.maximumDeviation === 'number'
    && Number.isFinite(mate.concentricMisalignment.maximumDeviation)
    && mate.concentricMisalignment.maximumDeviation >= 0;
}

/**
 * 写入同轴心错位校验策略。
 *
 * @param policies 策略表。
 * @param mateId Mate ID。
 * @param maximumDeviation 最大允许偏差。
 */
function appendConcentricMisalignmentValidationPolicy(
  policies: Map<string, ConcentricMisalignmentValidationPolicy>,
  mateId: string,
  maximumDeviation: number,
): void {
  const existing = policies.get(mateId);
  policies.set(mateId, {
    maximumDeviation: existing
      ? Math.min(existing.maximumDeviation, maximumDeviation)
      : maximumDeviation,
  });
}

/**
 * 构建同轴心错位最终残差校验策略。
 *
 * 设计说明：
 * SOLIDWORKS 的错位同轴心 Mate 会让一对 Concentric Mate 中的一条或两条保留可控偏差。
 * 求解后 validator 不能再用普通 Concentric 的“必须精确同轴”规则校验被分配偏差的 Mate，
 * 否则 Align this / Align linked / Symmetric 都会被误报为 over-defined。
 *
 * @param mateDefinitions 本轮求解 Mate 列表。
 * @returns 按 Mate ID 索引的允许偏差策略。
 */
function createConcentricMisalignmentValidationPolicyMap(
  mateDefinitions: readonly AssemblyMateDefinition[],
): ReadonlyMap<string, ConcentricMisalignmentValidationPolicy> {
  const mateIds = new Set(mateDefinitions.map((mate) => mate.mateId));
  const policies = new Map<string, ConcentricMisalignmentValidationPolicy>();

  mateDefinitions.forEach((mate) => {
    if (!isValidConcentricMisalignmentMate(mate)) {
      return;
    }
    const linkedMateId = mate.concentricMisalignment!.linkedMateId;
    const maximumDeviation = mate.concentricMisalignment!.maximumDeviation;
    if (!mateIds.has(linkedMateId)) {
      return;
    }

    if (mate.concentricMisalignment!.type === MateConcentricMisalignmentType.AlignLinkedMate) {
      appendConcentricMisalignmentValidationPolicy(policies, mate.mateId, maximumDeviation);
      return;
    }
    if (mate.concentricMisalignment!.type === MateConcentricMisalignmentType.AlignThisMate) {
      appendConcentricMisalignmentValidationPolicy(policies, linkedMateId, maximumDeviation);
      return;
    }
    if (mate.concentricMisalignment!.type === MateConcentricMisalignmentType.Symmetric) {
      appendConcentricMisalignmentValidationPolicy(policies, mate.mateId, maximumDeviation);
      appendConcentricMisalignmentValidationPolicy(policies, linkedMateId, maximumDeviation);
    }
  });

  return policies;
}

/**
 * 约束校验输入。
 */
export interface StandardMateConstraintValidationInput {
  /** 原始配合定义。 */
  readonly mateDefinitions: readonly AssemblyMateDefinition[];
  /** 组件最终求解状态。 */
  readonly components: Readonly<Record<string, AssemblyMateSolverComponentState>>;
  /** 组件最终 transform。 */
  readonly componentTransforms: Readonly<Record<string, ComponentTransformSnapshot>>;
  /** 组件从选择时世界几何到最终几何的累计 delta。 */
  readonly componentDeltaMap: ComponentWorldDeltaMap;
  /** 线性尺寸换算比例。 */
  readonly linearDimensionScale?: number;
  /**
   * 尚未提交到装配体文档的临时 Mate ID。
   *
   * 说明：
   * 临时 Mate 是 PropertyManager 当前草稿，残差失败应优先表达为“当前配合未解”；
   * 只有已提交 Mate 被破坏或冗余自由度被检测到时，才进入 SOLIDWORKS 语义的过定义。
   */
  readonly transientMateIds?: readonly string[];
  /** 长度容差。 */
  readonly epsilon: number;
  /** 角度容差。 */
  readonly angleEpsilon?: number;
}

/**
 * 计算点到轴线的距离。
 *
 * @param point 点。
 * @param axisOrigin 轴上一点。
 * @param axisDirection 轴方向。
 * @returns 最短距离。
 */
function calculatePointAxisDistance(
  point: THREE.Vector3,
  axisOrigin: THREE.Vector3,
  axisDirection: THREE.Vector3,
): number {
  const originToPoint = point.clone().sub(axisOrigin);
  const projection = axisOrigin.clone().addScaledVector(axisDirection, originToPoint.dot(axisDirection));
  return point.distanceTo(projection);
}

/**
 * 将点投影到轴线上。
 *
 * @param point 待投影点。
 * @param axis 轴线几何。
 * @returns 轴线上的投影点。
 */
function projectPointToAxis(point: THREE.Vector3, axis: AxisGeometry): THREE.Vector3 {
  const originToPoint = point.clone().sub(axis.origin);
  return axis.origin.clone().addScaledVector(axis.direction, originToPoint.dot(axis.direction));
}

/**
 * 解析 Distance / Coincident / Concentric 校验中可按点处理的几何。
 *
 * @param entity Mate 实体。
 * @returns 点几何；无法按点处理时返回 null。
 */
function resolveDistancePointGeometry(entity: AssemblyMateDefinition['firstEntity']): { readonly point: THREE.Vector3 } | null {
  const point = resolvePointGeometry(entity);
  if (point) {
    return point;
  }
  const sphere = resolveSphereGeometry(entity);
  return sphere ? { point: sphere.center } : null;
}

/**
 * 判断轴线是否具有圆形距离语义。
 *
 * @param axis 轴线几何。
 * @param epsilon 长度容差。
 * @returns 是否存在有效半径。
 */
function hasRoundAxisDistanceGeometry(axis: AxisGeometry, epsilon: number): boolean {
  return typeof axis.radius === 'number' && Number.isFinite(axis.radius) && axis.radius > epsilon;
}

/**
 * 解析有符号距离的符号。
 *
 * @param value 有符号距离。
 * @param epsilon 容差。
 * @returns 距离符号；退化时返回 null。
 */
function resolveSignedDistanceSign(value: number, epsilon: number): number | null {
  if (!Number.isFinite(value) || Math.abs(value) <= epsilon) {
    return null;
  }
  return value < 0 ? -1 : 1;
}

/**
 * 解析圆形轴线用于距离残差校验的中心线参考点。
 *
 * @param mate 当前配合。
 * @param axis 轴线几何。
 * @param firstPlane 第一选择是否为平面。
 * @returns 用于距离校验的轴线参考点。
 */
function resolveAxisDistanceReferencePoint(
  mate: AssemblyMateDefinition,
  axis: AxisGeometry,
  firstPlane: boolean,
): THREE.Vector3 {
  const axisEntity = firstPlane ? mate.secondEntity : mate.firstEntity;
  const representativePoint = resolveRepresentativePointGeometry(axisEntity)?.point ?? null;
  return representativePoint ? projectPointToAxis(representativePoint, axis) : axis.origin.clone();
}

/**
 * 按 Mate 原始选择顺序解析平面-轴线距离所在侧。
 *
 * @param mate 创建 Mate 时的原始 Definition。
 * @param epsilon 容差。
 * @returns 选择侧符号；不适用时返回 null。
 */
function resolvePlaneAxisSelectionDistanceSign(
  mate: AssemblyMateDefinition,
  epsilon: number,
): number | null {
  const firstPlane = resolvePlaneGeometry(mate.firstEntity);
  const secondPlane = resolvePlaneGeometry(mate.secondEntity);
  const firstAxis = resolveAxisGeometry(mate.firstEntity);
  const secondAxis = resolveAxisGeometry(mate.secondEntity);

  if (firstPlane && secondAxis) {
    const axisReferencePoint = hasRoundAxisDistanceGeometry(secondAxis, epsilon)
      ? resolveAxisDistanceReferencePoint(mate, secondAxis, true)
      : secondAxis.origin;
    return resolveSignedDistanceSign(
      axisReferencePoint.sub(firstPlane.point).dot(firstPlane.normal),
      epsilon,
    );
  }

  if (firstAxis && secondPlane) {
    const axisReferencePoint = hasRoundAxisDistanceGeometry(firstAxis, epsilon)
      ? resolveAxisDistanceReferencePoint(mate, firstAxis, false)
      : firstAxis.origin;
    return resolveSignedDistanceSign(
      axisReferencePoint.sub(secondPlane.point).dot(secondPlane.normal),
      epsilon,
    );
  }

  return null;
}

/**
 * 解析平面-轴线 Distance 的稳定有符号尺寸。
 *
 * @param currentMate 当前求解完成后的 Mate Definition。
 * @param sourceMate 创建 Mate 时的原始 Mate Definition。
 * @param linearDimensionScale 线性尺寸换算比例。
 * @param epsilon 容差。
 * @returns 稳定有符号距离；不适用时返回 null。
 */
function resolvePlaneAxisSignedDistanceFromSelectionBranch(
  currentMate: AssemblyMateDefinition,
  sourceMate: AssemblyMateDefinition,
  linearDimensionScale: number,
  epsilon: number,
): number | null {
  if (currentMate.mateType !== AssemblyMateType.Distance) {
    return null;
  }
  const selectionSign = resolvePlaneAxisSelectionDistanceSign(sourceMate, epsilon);
  if (selectionSign === null) {
    return null;
  }
  const dimensionSign = currentMate.dimension?.flipped ? -selectionSign : selectionSign;
  const dimensionValue = Math.abs(resolveStandardMateSignedDistance(currentMate, linearDimensionScale));
  return dimensionSign * dimensionValue;
}

/**
 * 归一化向量；退化时返回 null。
 *
 * @param vector 待归一化向量。
 * @param epsilon 长度容差。
 * @returns 单位向量；长度退化时返回 null。
 */
function normalizeValidationVector(vector: THREE.Vector3, epsilon: number): THREE.Vector3 | null {
  if (vector.lengthSq() <= epsilon * epsilon) {
    return null;
  }
  return vector.normalize();
}

/**
 * 解析点到点 Distance 的选择侧方向。
 *
 * @param firstPoint 第一选择点。
 * @param secondPoint 第二选择点。
 * @param epsilon 长度容差。
 * @returns 从第一选择指向第二选择的单位方向；退化时返回 null。
 */
function resolvePointPointDistanceBranchDirection(
  firstPoint: THREE.Vector3,
  secondPoint: THREE.Vector3,
  epsilon: number,
): THREE.Vector3 | null {
  return normalizeValidationVector(secondPoint.clone().sub(firstPoint), epsilon);
}

/**
 * 解析点到轴 Distance 的选择侧方向。
 *
 * @param point 点几何。
 * @param axis 轴线几何。
 * @param pointIsFirst 点是否为第一选择。
 * @param epsilon 长度容差。
 * @returns 按 Mate 选择顺序表达的径向单位方向；退化时返回 null。
 */
function resolvePointAxisDistanceBranchDirection(
  point: THREE.Vector3,
  axis: AxisGeometry,
  pointIsFirst: boolean,
  epsilon: number,
): THREE.Vector3 | null {
  const projection = projectPointToAxis(point, axis);
  const direction = pointIsFirst
    ? projection.sub(point)
    : point.clone().sub(projection);
  return normalizeValidationVector(direction, epsilon);
}

/**
 * 校验点状 Distance 是否仍位于 SOLIDWORKS 创建侧。
 *
 * 设计说明：
 * 1. 点-点、点-线、球-线等 Distance 存在镜像解，绝对距离相同不代表约束满足；
 * 2. SOLIDWORKS 只有用户执行 Flip Dimension 时才切换尺寸侧；
 * 3. 因此最终残差校验必须复核“创建时第一选择到第二选择”的方向分支。
 *
 * @param currentMate 已按最终组件 transform 变换后的 Mate。
 * @param sourceMate 创建 Mate 时的原始 Mate。
 * @param epsilon 长度容差。
 * @returns 是否满足创建侧；不适用于当前几何时返回 null。
 */
function validatePointDistanceBranch(
  currentMate: AssemblyMateDefinition,
  sourceMate: AssemblyMateDefinition,
  epsilon: number,
): boolean | null {
  if (currentMate.mateType !== AssemblyMateType.Distance) {
    return null;
  }

  const sourceFirstPoint = resolveDistancePointGeometry(sourceMate.firstEntity);
  const sourceSecondPoint = resolveDistancePointGeometry(sourceMate.secondEntity);
  const currentFirstPoint = resolveDistancePointGeometry(currentMate.firstEntity);
  const currentSecondPoint = resolveDistancePointGeometry(currentMate.secondEntity);
  const sourceFirstAxis = resolveAxisGeometry(sourceMate.firstEntity);
  const sourceSecondAxis = resolveAxisGeometry(sourceMate.secondEntity);
  const currentFirstAxis = resolveAxisGeometry(currentMate.firstEntity);
  const currentSecondAxis = resolveAxisGeometry(currentMate.secondEntity);
  const flippedFactor = currentMate.dimension?.flipped ? -1 : 1;
  let sourceDirection: THREE.Vector3 | null = null;
  let currentDirection: THREE.Vector3 | null = null;

  if (sourceFirstPoint && sourceSecondPoint && currentFirstPoint && currentSecondPoint) {
    sourceDirection = resolvePointPointDistanceBranchDirection(sourceFirstPoint.point, sourceSecondPoint.point, epsilon);
    currentDirection = resolvePointPointDistanceBranchDirection(currentFirstPoint.point, currentSecondPoint.point, epsilon);
  } else if (sourceFirstPoint && sourceSecondAxis && currentFirstPoint && currentSecondAxis) {
    sourceDirection = resolvePointAxisDistanceBranchDirection(sourceFirstPoint.point, sourceSecondAxis, true, epsilon);
    currentDirection = resolvePointAxisDistanceBranchDirection(currentFirstPoint.point, currentSecondAxis, true, epsilon);
  } else if (sourceFirstAxis && sourceSecondPoint && currentFirstAxis && currentSecondPoint) {
    sourceDirection = resolvePointAxisDistanceBranchDirection(sourceSecondPoint.point, sourceFirstAxis, false, epsilon);
    currentDirection = resolvePointAxisDistanceBranchDirection(currentSecondPoint.point, currentFirstAxis, false, epsilon);
  }

  if (!sourceDirection || !currentDirection) {
    return null;
  }

  /**
   * 用方向点积判断分支，而不是重新计算带符号距离。点-轴距离在 3D 中没有天然标量符号，
   * 但创建时的径向单位方向足以区分 SOLIDWORKS 的尺寸侧与镜像侧。
   */
  return currentDirection.dot(sourceDirection.multiplyScalar(flippedFactor)) >= -epsilon;
}

/**
 * 计算两条轴线的最短距离。
 *
 * @param firstOrigin 第一条轴上一点。
 * @param firstDirection 第一条轴方向。
 * @param secondOrigin 第二条轴上一点。
 * @param secondDirection 第二条轴方向。
 * @param epsilon 容差。
 * @returns 轴线最短距离。
 */
function calculateAxisAxisDistance(
  firstOrigin: THREE.Vector3,
  firstDirection: THREE.Vector3,
  secondOrigin: THREE.Vector3,
  secondDirection: THREE.Vector3,
  epsilon: number,
): number {
  const cross = firstDirection.clone().cross(secondDirection);
  const originDelta = secondOrigin.clone().sub(firstOrigin);
  if (cross.lengthSq() <= epsilon * epsilon) {
    return originDelta.clone().sub(firstDirection.clone().multiplyScalar(originDelta.dot(firstDirection))).length();
  }
  return Math.abs(originDelta.dot(cross.normalize()));
}

/**
 * 判断两个方向是否满足指定夹角。
 *
 * @param first 第一方向。
 * @param second 第二方向。
 * @param expectedAngle 目标夹角。
 * @param angleEpsilon 角度容差。
 * @returns 是否满足。
 */
function isAngleSatisfied(
  first: THREE.Vector3,
  second: THREE.Vector3,
  expectedAngle: number,
  angleEpsilon: number,
): boolean {
  const actualAngle = first.angleTo(second);
  return Math.abs(actualAngle - expectedAngle) <= angleEpsilon;
}

/**
 * 判断平行方向是否满足。
 *
 * @param first 第一方向。
 * @param second 第二方向。
 * @param antiAligned 是否反向。
 * @param angleEpsilon 角度容差。
 * @returns 是否满足。
 */
function isParallelSatisfied(
  first: THREE.Vector3,
  second: THREE.Vector3,
  antiAligned: boolean,
  angleEpsilon: number,
): boolean {
  return isAngleSatisfied(first, second, antiAligned ? Math.PI : 0, angleEpsilon);
}

/**
 * 判断两个方向是否满足无向平行。
 *
 * @param first 第一方向。
 * @param second 第二方向。
 * @param angleEpsilon 角度容差。
 * @returns 是否同向或反向平行。
 */
function isUnsignedParallelSatisfied(
  first: THREE.Vector3,
  second: THREE.Vector3,
  angleEpsilon: number,
): boolean {
  return isAngleSatisfied(first, second, 0, angleEpsilon)
    || isAngleSatisfied(first, second, Math.PI, angleEpsilon);
}

/**
 * 解析 Mate 两侧组件对的稳定键。
 *
 * @param mate Mate 定义。
 * @returns 与实体顺序无关的组件对键。
 */
function resolveMateComponentPairKey(mate: AssemblyMateDefinition): string {
  return [
    mate.firstEntity.componentInstanceId,
    mate.secondEntity.componentInstanceId,
  ].sort().join('::');
}

/**
 * 判断 Mate 是否为普通同轴心约束。
 *
 * @param mate Mate 定义。
 * @returns 是否为未抑制、未启用错位策略的同轴心 Mate。
 */
function isPlainConcentricMate(mate: AssemblyMateDefinition): boolean {
  return mate.mateType === AssemblyMateType.Concentric
    && !mate.suppressed
    && !mate.concentricMisalignment;
}

/**
 * 判断平面重合是否处在“法向朝向被同轴心位姿锁定、符号无意义”的无向法向校验场景。
 *
 * 物理判据：贴合面法向的符号只有在“当前能改变它”时才有意义。对同一对零件：
 * 1. 已提交 ≥2 条同轴心 → 转动被完全锁死，法向唯一固定、无法翻转，符号无意义 → 恒按无向校验；
 * 2. 已提交 1 条圆孔同轴心且其轴向平行于贴合面法向 → 该重合其实**可翻转**（绕过轴心原点的垂直轴
 *    整翻 180°，圆孔同轴心的无向中心线仍重合），“同向/反向”是两个真实可达配置：
 *    - **瞬态**重合（正在 PropertyManager 配置）按 signed 校验，让切换对齐能立刻驱动第二零件翻转；
 *    - **已提交**重合按无向校验保持既有朝向，避免拖拽扰动时被强行翻转。
 *
 * @param sourceMate 当前原始 Mate。
 * @param mateDefinitions 当前求解图中的 Mate 定义。
 * @param transientMateIds 临时 Mate ID 集合。
 * @param angleEpsilon 角度容差。
 * @returns 是否应按无向平面法向校验。
 */
function shouldValidatePlaneCoincidentDirectionAsUnsigned(
  sourceMate: AssemblyMateDefinition,
  mateDefinitions: readonly AssemblyMateDefinition[],
  transientMateIds: ReadonlySet<string>,
  angleEpsilon: number,
): boolean {
  if (sourceMate.mateType !== AssemblyMateType.Coincident) {
    return false;
  }
  const componentPairKey = resolveMateComponentPairKey(sourceMate);
  const committedConcentrics = mateDefinitions.filter((candidateMate) => (
    !transientMateIds.has(candidateMate.mateId)
    && isPlainConcentricMate(candidateMate)
    && resolveMateComponentPairKey(candidateMate) === componentPairKey
  ));
  if (committedConcentrics.length >= 2) {
    // 已提交 ≥2 条同轴心把转动完全锁死，法向朝向唯一固定、无法翻转，符号无意义——恒按无向校验。
    return true;
  }
  const planeNormal = resolvePlaneGeometry(sourceMate.firstEntity)?.normal
    ?? resolvePlaneGeometry(sourceMate.secondEntity)?.normal;
  if (!planeNormal) {
    return false;
  }
  const hasAxisParallelConcentric = committedConcentrics.some((concentricMate) => {
    const axisDirection = resolveAxisGeometry(concentricMate.firstEntity)?.direction
      ?? resolveAxisGeometry(concentricMate.secondEntity)?.direction;
    return axisDirection ? isUnsignedParallelSatisfied(axisDirection, planeNormal, angleEpsilon) : false;
  });
  if (!hasAxisParallelConcentric) {
    return false;
  }
  /**
   * 单条圆孔同轴心 + 法向平行于轴：该重合其实**可翻转**——绕过轴心原点的垂直轴整体翻 180°，圆孔同轴心的
   * 无向中心线仍重合、依旧满足。因此“同向/反向”是两个真实可达的配置：
   * - 正在配置的**瞬态**重合按 signed 校验，让用户在 PropertyManager 切换对齐能立刻驱动第二零件翻转
   *   （否则无向校验会让残差优先短路、切换无反应，必须拖拽扰动才生效）；
   * - **已提交**重合按无向校验保持既有朝向，避免拖拽扰动时被强行翻转（与已提交同轴心保持朝向同理）。
   */
  return !transientMateIds.has(sourceMate.mateId);
}

/**
 * 从组件 transform 快照读取单位四元数。
 *
 * @param transform 组件 transform。
 * @returns 单位四元数。
 */
function quaternionFromTransformSnapshot(transform: ComponentTransformSnapshot): THREE.Quaternion {
  return new THREE.Quaternion(
    transform.quaternion[0],
    transform.quaternion[1],
    transform.quaternion[2],
    transform.quaternion[3],
  ).normalize();
}

/**
 * 解析配合两侧可用于方向校验的向量。
 *
 * @param mate 当前配合。
 * @returns 方向向量；无法解析时返回 null。
 */
interface DirectionPairValidationContext {
  /** 第一实体用于方向校验的向量。 */
  readonly firstDirection: THREE.Vector3;
  /** 第二实体用于方向校验的向量。 */
  readonly secondDirection: THREE.Vector3;
  /** 是否为平面与轴线组合。 */
  readonly isPlaneAxisPair: boolean;
}

/**
 * 解析配合两侧可用于方向校验的上下文。
 *
 * @param mate 当前配合。
 * @returns 方向校验上下文；无法解析时返回 null。
 */
function resolveDirectionPair(mate: AssemblyMateDefinition): DirectionPairValidationContext | null {
  const firstPlane = resolvePlaneGeometry(mate.firstEntity);
  const secondPlane = resolvePlaneGeometry(mate.secondEntity);
  const firstAxis = resolveAxisGeometry(mate.firstEntity);
  const secondAxis = resolveAxisGeometry(mate.secondEntity);
  const firstDirection = firstPlane?.normal ?? firstAxis?.direction ?? null;
  const secondDirection = secondPlane?.normal ?? secondAxis?.direction ?? null;
  if (!firstDirection || !secondDirection) {
    return null;
  }
  return {
    firstDirection,
    secondDirection,
    isPlaneAxisPair: Boolean((firstPlane && secondAxis) || (firstAxis && secondPlane)),
  };
}

/**
 * 校验方向类配合。
 *
 * @param mate 当前配合。
 * @param angleEpsilon 角度容差。
 * @returns 是否满足；无法校验时返回 null。
 */
function validateDirectionalMate(
  mate: AssemblyMateDefinition,
  angleEpsilon: number,
): boolean | null {
  const directionContext = resolveDirectionPair(mate);
  if (!directionContext) {
    return null;
  }
  const { firstDirection, secondDirection, isPlaneAxisPair } = directionContext;
  if (mate.mateType === AssemblyMateType.Parallel) {
    if (isPlaneAxisPair) {
      return isAngleSatisfied(firstDirection, secondDirection, Math.PI / 2, angleEpsilon);
    }

    return isParallelSatisfied(
      firstDirection,
      secondDirection,
      mate.alignment === MateAlignment.AntiAligned,
      angleEpsilon,
    );
  }
  if (mate.mateType === AssemblyMateType.Perpendicular) {
    if (isPlaneAxisPair) {
      return isParallelSatisfied(
        firstDirection,
        secondDirection,
        mate.alignment === MateAlignment.AntiAligned,
        angleEpsilon,
      );
    }

    return isAngleSatisfied(firstDirection, secondDirection, Math.PI / 2, angleEpsilon);
  }
  if (mate.mateType === AssemblyMateType.Angle) {
    if (mate.referenceEntity) {
      return validateReferencedAngleMate(mate, firstDirection, secondDirection, angleEpsilon);
    }
    const alignedFirst = mate.alignment === MateAlignment.AntiAligned
      ? firstDirection.clone().multiplyScalar(-1)
      : firstDirection;
    return isAngleSatisfied(alignedFirst, secondDirection, mate.dimension?.value ?? 0, angleEpsilon);
  }
  return null;
}

/**
 * 校验平面与平面之间的偏移。
 *
 * @param mate 当前配合。
 * @param targetDistance 目标距离。
 * @param epsilon 长度容差。
 * @param angleEpsilon 角度容差。
 * @param validateDirection 是否校验轴线方向与平面关系。
 * @param validateUnsignedDirection 是否按无向平行校验平面法向。
 * @returns 是否满足；无法校验时返回 null。
 */
function validatePlanePlaneOffset(
  mate: AssemblyMateDefinition,
  targetDistance: number,
  epsilon: number,
  angleEpsilon: number,
  validateDirection: boolean,
  validateUnsignedDirection: boolean,
): boolean | null {
  const firstPlane = resolvePlaneGeometry(mate.firstEntity);
  const secondPlane = resolvePlaneGeometry(mate.secondEntity);
  if (!firstPlane || !secondPlane) {
    return null;
  }
  const directionSatisfied = !validateDirection || (
    validateUnsignedDirection
      ? isUnsignedParallelSatisfied(firstPlane.normal, secondPlane.normal, angleEpsilon)
      : isParallelSatisfied(
          firstPlane.normal,
          secondPlane.normal,
          mate.alignment === MateAlignment.AntiAligned,
          angleEpsilon,
        )
  );
  const signedDistance = secondPlane.point.clone().sub(firstPlane.point).dot(firstPlane.normal);
  /**
   * Distance Mate 的 Flip Dimension 是反转测量方向，不是单纯保持绝对间距。
   * 因此平面-平面距离必须校验有符号距离，才能识别“移动到另一侧”的约束。
   */
  const distanceSatisfied = mate.mateType === AssemblyMateType.Distance
    ? Math.abs(signedDistance - targetDistance) <= epsilon
    : Math.abs(Math.abs(signedDistance) - Math.abs(targetDistance)) <= epsilon;
  return directionSatisfied && distanceSatisfied;
}

/**
 * 校验平面与轴线之间的偏移。
 *
 * @param mate 当前配合。
 * @param targetDistance 目标距离。
 * @param epsilon 长度容差。
 * @param angleEpsilon 角度容差。
 * @param validateDirection 是否校验轴线方向与平面关系。
 * @param validateSignedDistance 是否校验有符号距离。
 * @returns 是否满足；无法校验时返回 null。
 */
function validatePlaneAxisOffset(
  mate: AssemblyMateDefinition,
  targetDistance: number,
  epsilon: number,
  angleEpsilon: number,
  validateDirection: boolean,
  validateSignedDistance: boolean,
): boolean | null {
  const firstPlane = resolvePlaneGeometry(mate.firstEntity);
  const secondPlane = resolvePlaneGeometry(mate.secondEntity);
  const firstAxis = resolveAxisGeometry(mate.firstEntity);
  const secondAxis = resolveAxisGeometry(mate.secondEntity);
  const plane = firstPlane ?? secondPlane;
  const axis = firstPlane ? secondAxis : firstAxis;
  if (!plane || !axis) {
    return null;
  }
  const parallelToPlane = !validateDirection || Math.abs(axis.direction.dot(plane.normal)) <= angleEpsilon;
  const representativePoint = firstPlane
    ? resolveRepresentativePointGeometry(mate.secondEntity)?.point
    : resolveRepresentativePointGeometry(mate.firstEntity)?.point;
  const distancePoint = validateDirection
    ? axis.origin
    : (mate.mateType === AssemblyMateType.Distance && hasRoundAxisDistanceGeometry(axis, epsilon)
      ? resolveAxisDistanceReferencePoint(mate, axis, Boolean(firstPlane))
      : representativePoint ?? axis.origin);
  const signedDistance = distancePoint.clone().sub(plane.point).dot(plane.normal);
  const distanceSatisfied = validateSignedDistance
    ? Math.abs(signedDistance - targetDistance) <= epsilon
    : Math.abs(Math.abs(signedDistance) - Math.abs(targetDistance)) <= epsilon;
  return parallelToPlane && distanceSatisfied;
}

/**
 * 判断实体是否为圆边。
 *
 * @param entity Mate 实体。
 * @returns 是否为圆边。
 */
function isCircularEdgeEntity(entity: MateEntityRef): boolean {
  return entity.entityKind === MateEntityKind.CircularEdge;
}

/**
 * 校验平面与圆边重合。
 *
 * 设计说明：
 * 圆边-平面 Coincident 应约束圆边所在平面与目标平面重合，因此要校验圆边法向和平面法向平行，
 * 且圆心位于平面上；不能使用通用平面-轴线“轴线在平面内”的校验。
 *
 * @param mate 当前配合。
 * @param epsilon 长度容差。
 * @param angleEpsilon 角度容差。
 * @returns 是否满足；不是平面-圆边组合时返回 null。
 */
function validatePlaneCircularEdgeCoincident(
  mate: AssemblyMateDefinition,
  epsilon: number,
  angleEpsilon: number,
): boolean | null {
  if (mate.mateType !== AssemblyMateType.Coincident) {
    return null;
  }
  const firstPlane = resolvePlaneGeometry(mate.firstEntity);
  const secondPlane = resolvePlaneGeometry(mate.secondEntity);
  const firstAxis = resolveAxisGeometry(mate.firstEntity);
  const secondAxis = resolveAxisGeometry(mate.secondEntity);
  if (firstPlane && secondAxis && isCircularEdgeEntity(mate.secondEntity)) {
    const normalSatisfied = Math.abs(Math.abs(secondAxis.direction.dot(firstPlane.normal)) - 1) <= angleEpsilon;
    const distance = Math.abs(secondAxis.origin.clone().sub(firstPlane.point).dot(firstPlane.normal));
    return normalSatisfied && distance <= epsilon;
  }
  if (firstAxis && isCircularEdgeEntity(mate.firstEntity) && secondPlane) {
    const normalSatisfied = Math.abs(Math.abs(firstAxis.direction.dot(secondPlane.normal)) - 1) <= angleEpsilon;
    const distance = Math.abs(firstAxis.origin.clone().sub(secondPlane.point).dot(secondPlane.normal));
    return normalSatisfied && distance <= epsilon;
  }
  return null;
}

/**
 * 校验轴线与轴线之间的偏移。
 *
 * @param mate 当前配合。
 * @param targetDistance 目标距离。
 * @param epsilon 长度容差。
 * @param angleEpsilon 角度容差。
 * @returns 是否满足；无法校验时返回 null。
 */
function validateAxisAxisOffset(
  mate: AssemblyMateDefinition,
  targetDistance: number,
  epsilon: number,
  angleEpsilon: number,
  validateDirection: boolean,
): boolean | null {
  const firstAxis = resolveAxisGeometry(mate.firstEntity);
  const secondAxis = resolveAxisGeometry(mate.secondEntity);
  if (!firstAxis || !secondAxis) {
    return null;
  }
  const directionSatisfied = !validateDirection || isParallelSatisfied(
    firstAxis.direction,
    secondAxis.direction,
    mate.alignment === MateAlignment.AntiAligned,
    angleEpsilon,
  );
  const distance = calculateAxisAxisDistance(
    firstAxis.origin,
    firstAxis.direction,
    secondAxis.origin,
    secondAxis.direction,
    epsilon,
  );
  return directionSatisfied && Math.abs(distance - Math.abs(targetDistance)) <= epsilon;
}

/**
 * 校验点相关的重合或距离配合。
 *
 * @param mate 当前配合。
 * @param targetDistance 目标距离。
 * @param epsilon 长度容差。
 * @returns 是否满足；无法校验时返回 null。
 */
function validatePointOffset(
  mate: AssemblyMateDefinition,
  sourceMate: AssemblyMateDefinition,
  targetDistance: number,
  epsilon: number,
  validateBranch: boolean,
): StandardMateConstraintValidationResult | null {
  const firstPoint = resolveDistancePointGeometry(mate.firstEntity);
  const secondPoint = resolveDistancePointGeometry(mate.secondEntity);
  const firstPlane = resolvePlaneGeometry(mate.firstEntity);
  const secondPlane = resolvePlaneGeometry(mate.secondEntity);
  const firstAxis = resolveAxisGeometry(mate.firstEntity);
  const secondAxis = resolveAxisGeometry(mate.secondEntity);
  if (firstPoint && secondPoint) {
    const distanceSatisfied = Math.abs(firstPoint.point.distanceTo(secondPoint.point) - Math.abs(targetDistance)) <= epsilon;
    const branchSatisfied = validateBranch
      ? validatePointDistanceBranch(mate, sourceMate, epsilon)
      : null;
    return createPointDistanceValidationResult(distanceSatisfied, branchSatisfied);
  }
  const point = firstPoint ?? secondPoint;
  const plane = firstPoint ? secondPlane : firstPlane;
  const axis = firstPoint ? secondAxis : firstAxis;
  if (point && plane) {
    const distance = Math.abs(point.point.clone().sub(plane.point).dot(plane.normal));
    return createConstraintValidationResult(Math.abs(distance - Math.abs(targetDistance)) <= epsilon);
  }
  if (point && axis) {
    const distance = calculatePointAxisDistance(point.point, axis.origin, axis.direction);
    const branchSatisfied = validateBranch
      ? validatePointDistanceBranch(mate, sourceMate, epsilon)
      : null;
    return createPointDistanceValidationResult(
      Math.abs(distance - Math.abs(targetDistance)) <= epsilon,
      branchSatisfied,
    );
  }
  return null;
}

/**
 * 校验同轴心配合。
 *
 * @param mate 当前配合。
 * @param epsilon 长度容差。
 * @param angleEpsilon 角度容差。
 * @returns 是否满足；无法校验时返回 null。
 */
function validateConcentricMate(
  mate: AssemblyMateDefinition,
  epsilon: number,
  angleEpsilon: number,
  misalignmentPolicy?: ConcentricMisalignmentValidationPolicy,
): boolean | null {
  return evaluateStandardMateConcentricResidual({
    mate,
    tolerance: {
      linear: epsilon,
      angular: angleEpsilon,
    },
    maximumRadialDeviation: misalignmentPolicy?.maximumDeviation,
  }).satisfied;
}

/**
 * 校验同轴心锁旋转是否保持选择瞬间的组件相对姿态。
 *
 * @param mate 当前配合。
 * @param componentTransforms 组件最终 transform。
 * @param angleEpsilon 角度容差。
 * @returns 是否满足；缺少选择基线或组件 transform 时返回 null。
 */
function validateConcentricLockRotation(
  mate: AssemblyMateDefinition,
  componentTransforms: Readonly<Record<string, ComponentTransformSnapshot>>,
  angleEpsilon: number,
): boolean | null {
  if (!mate.lockRotation) {
    return true;
  }
  const firstSelectionTransform = mate.firstEntity.componentTransformAtSelection;
  const secondSelectionTransform = mate.secondEntity.componentTransformAtSelection;
  const firstCurrentTransform = componentTransforms[mate.firstEntity.componentInstanceId];
  const secondCurrentTransform = componentTransforms[mate.secondEntity.componentInstanceId];
  if (!firstSelectionTransform || !secondSelectionTransform || !firstCurrentTransform || !secondCurrentTransform) {
    return null;
  }

  const expectedRelative = quaternionFromTransformSnapshot(firstSelectionTransform)
    .invert()
    .multiply(quaternionFromTransformSnapshot(secondSelectionTransform))
    .normalize();
  const actualRelative = quaternionFromTransformSnapshot(firstCurrentTransform)
    .invert()
    .multiply(quaternionFromTransformSnapshot(secondCurrentTransform))
    .normalize();

  return actualRelative.angleTo(expectedRelative) <= angleEpsilon;
}

/**
 * 校验轴线与球之间的相切。
 *
 * @param axisEntity 轴线实体。
 * @param axis 轴线几何。
 * @param sphere 球几何。
 * @param epsilon 长度容差。
 * @returns 是否满足；轴线缺少固定半径语义时返回 null。
 */
function validateAxisSphereTangent(
  mate: AssemblyMateDefinition,
  axisEntity: MateEntityRef,
  axis: AxisGeometry,
  sphere: SphereGeometry,
  epsilon: number,
): boolean | null {
  const axisRadius = resolveAxisTangentRadius(axisEntity, axis);
  if (axisRadius === null) {
    return null;
  }
  if (isRoundRoundTangentInternalBranchDegenerate(mate, axisRadius, sphere.radius, epsilon)) {
    return false;
  }
  const distance = calculatePointAxisDistance(sphere.center, axis.origin, axis.direction);
  const targetDistance = resolveAlignedRoundRoundTangentDistance(mate, axisRadius, sphere.radius);
  return Math.abs(distance - targetDistance) <= epsilon;
}

/**
 * 校验两条轴线之间的相切。
 *
 * 设计说明：
 * SOLIDWORKS 允许直线 / 基准轴与圆柱、球等圆形几何建立 Tangent Mate。
 * 求解器通过 `resolveAxisTangentRadius` 把直线和基准轴视为零半径轴线；
 * 校验器必须复用同一语义，否则已求解成功的线-圆柱 Tangent 会被误判为不支持几何。
 *
 * @param firstEntity 第一轴线实体。
 * @param firstAxis 第一轴线几何。
 * @param secondEntity 第二轴线实体。
 * @param secondAxis 第二轴线几何。
 * @param epsilon 长度容差。
 * @returns 是否满足；任一轴线缺少相切半径语义时返回 null。
 */
function validateAxisAxisTangent(
  mate: AssemblyMateDefinition,
  firstEntity: MateEntityRef,
  firstAxis: AxisGeometry,
  secondEntity: MateEntityRef,
  secondAxis: AxisGeometry,
  epsilon: number,
): boolean | null {
  const firstRadius = resolveAxisTangentRadius(firstEntity, firstAxis);
  const secondRadius = resolveAxisTangentRadius(secondEntity, secondAxis);
  if (firstRadius === null || secondRadius === null) {
    return null;
  }
  if (Math.max(Math.abs(firstRadius), Math.abs(secondRadius)) <= epsilon) {
    return null;
  }
  if (isRoundRoundTangentInternalBranchDegenerate(mate, firstRadius, secondRadius, epsilon)) {
    return false;
  }
  const distance = calculateAxisAxisDistance(
    firstAxis.origin,
    firstAxis.direction,
    secondAxis.origin,
    secondAxis.direction,
    epsilon,
  );
  const targetDistance = resolveAlignedRoundRoundTangentDistance(mate, firstRadius, secondRadius);
  return Math.abs(distance - targetDistance) <= epsilon;
}

/**
 * 校验两个球之间的相切。
 *
 * @param first 第一球。
 * @param second 第二球。
 * @param epsilon 长度容差。
 * @returns 是否满足。
 */
function validateSphereSphereTangent(
  mate: AssemblyMateDefinition,
  first: SphereGeometry,
  second: SphereGeometry,
  epsilon: number,
): boolean {
  if (isRoundRoundTangentInternalBranchDegenerate(mate, first.radius, second.radius, epsilon)) {
    return false;
  }
  const targetDistance = resolveAlignedRoundRoundTangentDistance(mate, first.radius, second.radius);
  return Math.abs(first.center.distanceTo(second.center) - targetDistance) <= epsilon;
}

/**
 * 校验圆锥与球之间的相切。
 *
 * @param cone 圆锥几何。
 * @param sphere 球几何。
 * @param epsilon 长度容差。
 * @returns 是否满足；圆锥参数退化时返回 null。
 */
function validateConeSphereTangent(
  cone: ConeGeometry,
  sphere: SphereGeometry,
  epsilon: number,
): boolean | null {
  const distanceResult = resolveConeSphereSignedDistance(cone, sphere.center, epsilon);
  if (!distanceResult) {
    return null;
  }
  return Math.abs(Math.abs(distanceResult.signedDistance) - sphere.radius) <= epsilon;
}

/**
 * 校验平面与圆形实体参考点的有符号相切距离。
 *
 * @param mate 当前 Mate 定义。
 * @param plane 平面几何。
 * @param roundReferencePoint 圆柱轴心、圆边中心或球心等参考点。
 * @param radius 相切半径。
 * @param epsilon 长度容差。
 * @returns 是否满足指定相切侧。
 */
function validatePlaneRoundTangentSignedDistance(
  mate: AssemblyMateDefinition,
  plane: PlaneGeometry,
  roundReferencePoint: THREE.Vector3,
  radius: number,
  epsilon: number,
): boolean {
  const signedDistance = roundReferencePoint.clone().sub(plane.point).dot(plane.normal);
  const targetDistance = resolveAlignedPlaneRoundTangentDistance(mate, radius);
  return Math.abs(signedDistance - targetDistance) <= epsilon;
}

/**
 * 校验相切配合。
 *
 * @param mate 当前配合。
 * @param epsilon 长度容差。
 * @param angleEpsilon 角度容差。
 * @returns 是否满足；无法校验时返回 null。
 */
function validateTangentMate(
  mate: AssemblyMateDefinition,
  epsilon: number,
  angleEpsilon: number,
): boolean | null {
  const firstPlane = resolvePlaneGeometry(mate.firstEntity);
  const secondPlane = resolvePlaneGeometry(mate.secondEntity);
  const firstAxis = resolveAxisGeometry(mate.firstEntity);
  const secondAxis = resolveAxisGeometry(mate.secondEntity);
  const firstCone = resolveConeGeometry(mate.firstEntity);
  const secondCone = resolveConeGeometry(mate.secondEntity);
  const firstSphere = resolveSphereGeometry(mate.firstEntity);
  const secondSphere = resolveSphereGeometry(mate.secondEntity);
  const plane = firstPlane ?? secondPlane;
  const cone = firstPlane ? secondCone : firstCone;
  const roundAxis = firstPlane ? secondAxis : firstAxis;
  const roundSphere = firstPlane ? secondSphere : firstSphere;
  if (plane && cone) {
    const apexDistance = Math.abs(cone.apex.clone().sub(plane.point).dot(plane.normal));
    const expectedDot = Math.sin(cone.halfAngle);
    const actualDot = Math.abs(cone.direction.dot(plane.normal));
    return apexDistance <= epsilon && Math.abs(actualDot - expectedDot) <= angleEpsilon;
  }
  if (plane && roundAxis && typeof roundAxis.radius === 'number') {
    const directionSatisfied = Math.abs(roundAxis.direction.dot(plane.normal)) <= angleEpsilon;
    return directionSatisfied && validatePlaneRoundTangentSignedDistance(
      mate,
      plane,
      roundAxis.origin,
      roundAxis.radius,
      epsilon,
    );
  }
  if (plane && roundSphere) {
    return validatePlaneRoundTangentSignedDistance(
      mate,
      plane,
      roundSphere.center,
      roundSphere.radius,
      epsilon,
    );
  }
  if (firstCone && secondSphere) {
    return validateConeSphereTangent(firstCone, secondSphere, epsilon);
  }
  if (secondCone && firstSphere) {
    return validateConeSphereTangent(secondCone, firstSphere, epsilon);
  }
  if (firstAxis && secondSphere) {
    return validateAxisSphereTangent(mate, mate.firstEntity, firstAxis, secondSphere, epsilon);
  }
  if (secondAxis && firstSphere) {
    return validateAxisSphereTangent(mate, mate.secondEntity, secondAxis, firstSphere, epsilon);
  }
  if (firstSphere && secondSphere) {
    return validateSphereSphereTangent(mate, firstSphere, secondSphere, epsilon);
  }
  if (firstAxis && secondAxis) {
    return validateAxisAxisTangent(mate, mate.firstEntity, firstAxis, mate.secondEntity, secondAxis, epsilon);
  }
  return null;
}

/**
 * 判断矩阵是否在容差内相等。
 *
 * @param first 第一矩阵。
 * @param second 第二矩阵。
 * @param epsilon 容差。
 * @returns 是否相等。
 */
function areMatricesClose(first: THREE.Matrix4, second: THREE.Matrix4, epsilon: number): boolean {
  return first.elements.every((value, index) => Math.abs(value - second.elements[index]) <= epsilon);
}

/**
 * 校验锁定配合。
 *
 * @param mate 当前配合。
 * @param componentTransforms 组件最终 transform。
 * @param epsilon 容差。
 * @returns 是否满足；无法校验时返回 null。
 */
function validateLockMate(
  mate: AssemblyMateDefinition,
  componentTransforms: Readonly<Record<string, ComponentTransformSnapshot>>,
  epsilon: number,
): boolean | null {
  if (!mate.lockedRelativeTransform) {
    return null;
  }
  const firstTransform = componentTransforms[mate.firstEntity.componentInstanceId];
  const secondTransform = componentTransforms[mate.secondEntity.componentInstanceId];
  if (!firstTransform || !secondTransform) {
    return null;
  }
  const actualRelative = composeMatrix(firstTransform).invert().multiply(composeMatrix(secondTransform));
  const expectedRelative = composeMatrix(mate.lockedRelativeTransform.transform);
  return areMatricesClose(actualRelative, expectedRelative, epsilon);
}

/**
 * 校验单条配合是否在最终几何上仍然成立。
 *
 * @param mate 最终几何下的当前配合。
 * @param sourceMate 创建 Mate 时的原始配合。
 * @param componentTransforms 组件最终 transform。
 * @param epsilon 长度容差。
 * @param angleEpsilon 角度容差。
 * @returns 是否满足；无法校验时返回 null。
 */
function validateMateDefinition(
  mate: AssemblyMateDefinition,
  sourceMate: AssemblyMateDefinition,
  mateDefinitions: readonly AssemblyMateDefinition[],
  transientMateIds: ReadonlySet<string>,
  componentTransforms: Readonly<Record<string, ComponentTransformSnapshot>>,
  epsilon: number,
  angleEpsilon: number,
  linearDimensionScale: number,
  validatePointDistanceBranch: boolean,
  concentricMisalignmentValidationPolicies: ReadonlyMap<string, ConcentricMisalignmentValidationPolicy>,
): StandardMateConstraintValidationResult {
  if (mate.suppressed) {
    return createConstraintValidationResult(true);
  }
  if (
    mate.mateType === AssemblyMateType.Parallel
    || mate.mateType === AssemblyMateType.Perpendicular
    || mate.mateType === AssemblyMateType.Angle
  ) {
    return createConstraintValidationResult(validateDirectionalMate(mate, angleEpsilon));
  }
  if (mate.mateType === AssemblyMateType.Concentric) {
    const concentricSatisfied = validateConcentricMate(
      mate,
      epsilon,
      angleEpsilon,
      concentricMisalignmentValidationPolicies.get(mate.mateId),
    );
    if (concentricSatisfied !== true) {
      return createConstraintValidationResult(concentricSatisfied);
    }
    return createConstraintValidationResult(validateConcentricLockRotation(mate, componentTransforms, angleEpsilon));
  }
  if (mate.mateType === AssemblyMateType.Tangent) {
    return createConstraintValidationResult(validateTangentMate(mate, epsilon, angleEpsilon));
  }
  if (mate.mateType === AssemblyMateType.Lock) {
    return createConstraintValidationResult(validateLockMate(mate, componentTransforms, epsilon));
  }
  if (mate.mateType === AssemblyMateType.Coincident || mate.mateType === AssemblyMateType.Distance) {
    const planeAxisSignedDistance = resolvePlaneAxisSignedDistanceFromSelectionBranch(
      mate,
      sourceMate,
      linearDimensionScale,
      epsilon,
    );
    const signedDistance = planeAxisSignedDistance
      ?? resolveStandardMateSignedDistance(mate, linearDimensionScale);
    const targetDistance = mate.mateType === AssemblyMateType.Distance
      ? resolveStandardMateDistanceOffsetForSolver({
          mate,
          firstEntity: mate.firstEntity,
          secondEntity: mate.secondEntity,
          signedDistance,
        })
      : signedDistance;
    if (targetDistance === null) {
      return createConstraintValidationResult(null);
    }
    /**
     * 平面-平面 Distance 在 SOLIDWORKS 中允许同向/反向对齐，校验阶段也必须
     * 同步检查法向；平面-轴线 Distance 额外校验创建侧符号，避免绝对距离相等但位于错误一侧。
     */
    const validateDirection = mate.mateType === AssemblyMateType.Coincident
      || (mate.mateType === AssemblyMateType.Distance
        && Boolean(resolvePlaneGeometry(mate.firstEntity) && resolvePlaneGeometry(mate.secondEntity)));
    /**
     * 两条已提交同轴心会把组件姿态锁到当前法向分支；后续平面重合只需要验证两个无限平面已共面，
     * 不能再用默认 Aligned 分支把反向平行但物理可解的状态判成残差失败。
     */
    const validateUnsignedPlaneDirection = shouldValidatePlaneCoincidentDirectionAsUnsigned(
      sourceMate,
      mateDefinitions,
      transientMateIds,
      angleEpsilon,
    );
    const planePlaneResult = validatePlanePlaneOffset(
      mate,
      targetDistance,
      epsilon,
      angleEpsilon,
      validateDirection,
      validateUnsignedPlaneDirection,
    );
    if (planePlaneResult !== null) {
      return createConstraintValidationResult(planePlaneResult);
    }
    const planeCircularEdgeResult = validatePlaneCircularEdgeCoincident(mate, epsilon, angleEpsilon);
    if (planeCircularEdgeResult !== null) {
      return createConstraintValidationResult(planeCircularEdgeResult);
    }
    const surfaceCoincidentResult = validateSurfaceCoincidentMate(mate, epsilon, angleEpsilon);
    if (surfaceCoincidentResult !== null) {
      return createConstraintValidationResult(surfaceCoincidentResult);
    }
    const planeAxisResult = validatePlaneAxisOffset(
      mate,
      targetDistance,
      epsilon,
      angleEpsilon,
      validateDirection,
      planeAxisSignedDistance !== null,
    );
    if (planeAxisResult !== null) {
      return createConstraintValidationResult(planeAxisResult);
    }
    const axisAxisResult = validateAxisAxisOffset(mate, targetDistance, epsilon, angleEpsilon, validateDirection);
    if (axisAxisResult !== null) {
      return createConstraintValidationResult(axisAxisResult);
    }
    const pointOffsetResult = validatePointOffset(
      mate,
      sourceMate,
      targetDistance,
      epsilon,
      validatePointDistanceBranch,
    );
    return pointOffsetResult ?? createConstraintValidationResult(null);
  }
  return createConstraintValidationResult(null);
}

/**
 * 解析残差失败应返回的求解状态。
 *
 * 设计说明：
 * 单条 Mate 的最终残差失败只说明当前求解器没有把该 Mate 落到可满足状态，它本身不构成
 * SOLIDWORKS 语义里的“过定义”。PropertyManager 预览中的临时 Mate 也是同理：
 * 如果输入包含 PropertyManager 临时 Mate，任何由本轮草稿预览引出的残差都只能说明
 * 当前草稿不能确认；只有已提交 Mate 图自身残差失败时，才升级为过定义。
 *
 * @param input 残差失败上下文。
 * @returns 残差失败应暴露给业务层的状态。
 */
function resolveResidualFailureStatus(input: {
  readonly mate: AssemblyMateDefinition;
  readonly failureKind: StandardMateResidualFailureKind;
  readonly mateDefinitions: readonly AssemblyMateDefinition[];
  readonly transientMateIds: ReadonlySet<string>;
}): MateSolveStatus {
  if (input.transientMateIds.has(input.mate.mateId)) {
    /**
     * PropertyManager 草稿 Mate 还没有进入装配体文档。
     * 即使它与已提交 Mate 约束同一自由度，也只能表示“当前 Mate 不能确认”，不能把文档链路提前标记为过定义。
     */
    return MateSolveStatus.Unsolved;
  }

  return hasResidualConflictPeer({
    mate: input.mate,
    failureKind: input.failureKind,
    mateDefinitions: input.mateDefinitions,
    transientMateIds: input.transientMateIds,
  })
    ? MateSolveStatus.OverDefined
    : MateSolveStatus.Unsolved;
}

/**
 * 读取当前 PropertyManager 预览中的首个临时 Mate。
 *
 * @param mateDefinitions 本轮参与求解的 Mate 定义。
 * @param transientMateIds 临时 Mate ID 集合。
 * @returns 临时 Mate；没有临时 Mate 时返回 null。
 */
function resolveTransientPreviewMate(
  mateDefinitions: readonly AssemblyMateDefinition[],
  transientMateIds: ReadonlySet<string>,
): AssemblyMateDefinition | null {
  return mateDefinitions.find((mate) => transientMateIds.has(mate.mateId)) ?? null;
}

/**
 * 解析 Mate 类型在诊断中的用户可读名称。
 *
 * @param mateType Mate 类型。
 * @returns 中文 Mate 类型名称。
 */
function resolveMateTypeDiagnosticLabel(mateType: AssemblyMateType): string {
  switch (mateType) {
    case AssemblyMateType.Coincident:
      return '重合配合';
    case AssemblyMateType.Parallel:
      return '平行配合';
    case AssemblyMateType.Perpendicular:
      return '垂直配合';
    case AssemblyMateType.Tangent:
      return '相切配合';
    case AssemblyMateType.Concentric:
      return '同轴心配合';
    case AssemblyMateType.Lock:
      return '锁定配合';
    case AssemblyMateType.Distance:
      return '距离配合';
    case AssemblyMateType.Angle:
      return '角度配合';
    default:
      return '标准配合';
  }
}

/**
 * 解析残差失败诊断文案。
 *
 * @param status 残差失败状态。
 * @param mateType 触发残差失败的 Mate 类型。
 * @returns 可展示给 UI 的诊断文案。
 */
function resolveResidualFailureMessage(
  status: MateSolveStatus,
  mateType: AssemblyMateType,
): string {
  const label = resolveMateTypeDiagnosticLabel(mateType);
  return status === MateSolveStatus.OverDefined
    ? `${label}求解后的最终几何不再满足该约束，当前装配体配合链路过定义。`
    : `${label}求解后的最终几何不满足该约束，当前配合未能完成求解。`;
}

/**
 * 创建临时预览破坏既有 Mate 时的诊断。
 *
 * 设计说明：
 * PropertyManager 草稿尚未写入 Mate 文档。若它的预览位姿让已提交 Mate 残差失败，
 * 业务含义是“当前草稿不能确认”，不是“装配体文档已经过定义”。因此诊断必须挂到
 * 临时 Mate 上，让 UI 和提交逻辑按当前草稿未解处理。
 *
 * @param input 诊断输入。
 * @returns 临时 Mate 诊断。
 */
function createTransientPreviewCommittedResidualDiagnostic(input: {
  /** 当前 PropertyManager 临时 Mate。 */
  readonly transientMate: AssemblyMateDefinition;
  /** 被临时预览破坏的已提交 Mate。 */
  readonly committedMate: AssemblyMateDefinition;
}): AssemblyMateSolveDiagnostic {
  const transientLabel = resolveMateTypeDiagnosticLabel(input.transientMate.mateType);
  const committedLabel = resolveMateTypeDiagnosticLabel(input.committedMate.mateType);
  return {
    mateId: input.transientMate.mateId,
    status: MateSolveStatus.Unsolved,
    message: `当前临时${transientLabel}会破坏已提交${committedLabel}，请修改配合类型、实体或尺寸后再确认。`,
  };
}

/**
 * 判断已提交 Mate 残差是否应归因到当前临时预览。
 *
 * @param input 判断输入。
 * @returns 是否应归因到临时 Mate。
 */
function shouldAttributeCommittedResidualToTransientPreview(input: {
  /** 当前残差对应的原始 Mate。 */
  readonly sourceMate: AssemblyMateDefinition;
  /** 当前 PropertyManager 临时 Mate。 */
  readonly transientMate: AssemblyMateDefinition | null;
  /** 当前 PropertyManager 临时 Mate ID 集合。 */
  readonly transientMateIds: ReadonlySet<string>;
}): boolean {
  return Boolean(input.transientMate)
    && !input.transientMateIds.has(input.sourceMate.mateId);
}

/**
 * 判断当前残差是否属于 PropertyManager 临时 Mate 自身未解后的派生残差。
 *
 * 设计说明：
 * 预览求解会把已提交 Mate 和当前草稿 Mate 放到同一次试算中。若草稿 Mate 自身已经未解，
 * 本轮试算产生的组件姿态不再是可靠的“最终装配姿态”；此时已提交 Mate 的残差只能说明
 * 当前草稿扰动了试算过程，不应升级为 SOLIDWORKS 语义里的装配体过定义。
 *
 * @param input 判断上下文。
 * @returns 是否应忽略该派生残差。
 */
function shouldIgnoreCommittedResidualWhileTransientUnsolved(input: {
  /** 当前残差对应的原始 Mate。 */
  readonly sourceMate: AssemblyMateDefinition;
  /** 当前预览是否存在临时 Mate 自身残差失败。 */
  readonly hasTransientResidualFailure: boolean;
  /** 当前 PropertyManager 临时 Mate ID 集合。 */
  readonly transientMateIds: ReadonlySet<string>;
}): boolean {
  return input.hasTransientResidualFailure
    && !input.transientMateIds.has(input.sourceMate.mateId);
}

/**
 * 校验全部已求解配合的最终残差。
 *
 * @param input 校验输入。
 * @returns 残差或冗余诊断集合。
 */
export function validateSolvedMateDefinitions(
  input: StandardMateConstraintValidationInput,
): readonly AssemblyMateSolveDiagnostic[] {
  const angleEpsilon = input.angleEpsilon ?? DEFAULT_VALIDATION_ANGLE_EPSILON;
  const linearDimensionScale = resolveStandardMateLinearDimensionScale(input.linearDimensionScale);
  const validatePointDistanceBranch = input.mateDefinitions.length > 1;
  const transientMateIds = new Set(input.transientMateIds ?? []);
  const transientPreviewMate = resolveTransientPreviewMate(input.mateDefinitions, transientMateIds);
  const concentricMisalignmentValidationPolicies = createConcentricMisalignmentValidationPolicyMap(
    input.mateDefinitions,
  );
  const residualValidationResults = input.mateDefinitions
    .map((sourceMate) => ({
      sourceMate,
      solvedMate: transformMateDefinitionForSolve(sourceMate, input.componentDeltaMap, input.components),
    }))
    .map(({ sourceMate, solvedMate }) => ({
      sourceMate,
      solvedMate,
      validationResult: validateMateDefinition(
        solvedMate,
        sourceMate,
        input.mateDefinitions,
        transientMateIds,
        input.componentTransforms,
        input.epsilon,
        angleEpsilon,
        linearDimensionScale,
        validatePointDistanceBranch,
        concentricMisalignmentValidationPolicies,
      ),
    }));
  const hasTransientResidualFailure = residualValidationResults.some((result) => (
    transientMateIds.has(result.sourceMate.mateId)
    && result.validationResult.satisfied !== true
  ));
  const residualDiagnostics = residualValidationResults
    .map(({ sourceMate, solvedMate, validationResult }): AssemblyMateSolveDiagnostic | null => {
      if (shouldIgnoreCommittedResidualWhileTransientUnsolved({
        sourceMate,
        hasTransientResidualFailure,
        transientMateIds,
      })) {
        return null;
      }
      if (validationResult.satisfied === true) {
        return null;
      }
      if (validationResult.satisfied === null) {
        return {
          mateId: solvedMate.mateId,
          status: MateSolveStatus.UnsupportedGeometry,
          message: '配合求解后的最终几何无法被当前校验器验证，已按不支持几何处理。',
        };
      }
      if (shouldAttributeCommittedResidualToTransientPreview({
        sourceMate,
        transientMate: transientPreviewMate,
        transientMateIds,
      }) && transientPreviewMate) {
        return createTransientPreviewCommittedResidualDiagnostic({
          transientMate: transientPreviewMate,
          committedMate: sourceMate,
        });
      }
      const status = resolveResidualFailureStatus({
        mate: sourceMate,
        failureKind: validationResult.failureKind,
        mateDefinitions: input.mateDefinitions,
        transientMateIds,
      });
      return {
        mateId: solvedMate.mateId,
        status,
        message: resolveResidualFailureMessage(status, sourceMate.mateType),
      };
    })
    .filter((item): item is AssemblyMateSolveDiagnostic => item !== null);

  return [
    ...validateRedundantMateDefinitions({
      mateDefinitions: input.mateDefinitions,
      transientMateIds: input.transientMateIds,
    }),
    ...residualDiagnostics,
  ];
}
